import styled, { css } from 'styled-components';

/**
 * The whole row is the button: a notification has one thing to do with it.
 *
 * Two looks for one row. In the dropdown it is a line of a white panel and
 * only shows itself on hover; on the full page it is a card of its own, the
 * way every list in the admin is a stack of white cards on the cream page.
 */
export const RowButton = styled.button<{ $compact?: boolean }>`
  /* The containing block of its VisuallyHidden label. Without it the label
     is placed against the dropdown itself, outside the list that scrolls and
     clips it, and makes the whole dropdown taller than its panel - so the
     dropdown scrolled too, carrying See all up off its footer. */
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin: 0;
  border: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.ease200ms};

  ${({ $compact, theme }) =>
    $compact
      ? css`
          padding: 12px;
          border-radius: 16px;
          background: transparent;

          &:hover,
          &:focus-visible {
            background: ${theme.gray};
          }
        `
      : css`
          /* The same card as Box, shadow and border included, so the list
             reads like every other list of cards in the admin. */
          padding: 16px 20px;
          margin-bottom: 8px;
          border-radius: 20px;
          border: ${theme.borders.border1};
          background: ${theme.white};
          box-shadow: 0px 20px 30px rgba(0, 0, 0, 0.1);

          @media (max-width: 800px) {
            padding: 12px 16px;
          }
        `}
`;

/**
 * Unread, as a red dot at the start of the title line. Kept in the layout
 * when read (just not painted), so read and unread titles start at the same x.
 */
export const UnreadDot = styled.span<{ $visible: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: ${({ theme, $visible }) =>
    $visible ? theme.primary : 'transparent'};
`;

export const RowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const RowTitleLine = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

/** Bold while unread, which is most of what tells the two apart at a glance. */
export const RowTitle = styled.span<{ $unread: boolean }>`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${({ $unread }) => ($unread ? 700 : 500)};
  color: ${({ theme }) => theme.darkGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowAge = styled.span`
  flex: none;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;

/**
 * The sentence the backend wrote. Clamped in the dropdown, where the panel is
 * narrow and the next row matters as much as this one; whole on the page.
 */
export const RowBody = styled.span<{ $clamp?: number; $unread: boolean }>`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme, $unread }) =>
    $unread ? theme.darkGray : theme.lightGray};
  overflow-wrap: anywhere;

  ${({ $clamp }) =>
    $clamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${$clamp};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

/** Read out, never drawn - the unread state for a screen reader. */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
